import { ROLE_CATEGORIES, roleIdSortIndex } from './roles.js';

/** Skills for autocomplete (subset; extend as needed). */
export type SkillOption = {
  en: string;
  pl: string;
  /** Matches a RoleOption.id from roles.ts, or 'general' for cross-discipline skills. */
  roleId: string;
};

export const GENERAL_SKILL_GROUP = { id: 'general', title: 'General', titlePl: 'Ogólne' };

export function findRoleMetaById(roleId: string): { title: string; titlePl: string } {
  if (roleId === GENERAL_SKILL_GROUP.id) return GENERAL_SKILL_GROUP;
  for (const cat of ROLE_CATEGORIES) {
    const found = cat.roles.find((r) => r.id === roleId);
    if (found) return found;
  }
  return GENERAL_SKILL_GROUP;
}

export { roleIdSortIndex };

export const SKILLS: SkillOption[] = [
  // —— General / cross-functional ——
  { en: 'A/B Testing', pl: 'Testy A/B', roleId: 'general' },
  { en: 'Agile', pl: 'Agile', roleId: 'general' },
  { en: 'Communication', pl: 'Komunikacja', roleId: 'general' },
  { en: 'Conflict Resolution', pl: 'Rozwiązywanie Konfliktów', roleId: 'general' },
  { en: 'Critical Thinking', pl: 'Myślenie Krytyczne', roleId: 'general' },
  { en: 'Cross-functional Collaboration', pl: 'Współpraca Międzyfunkcyjna', roleId: 'general' },
  { en: 'Documentation', pl: 'Dokumentacja', roleId: 'general' },
  { en: 'Git', pl: 'Git', roleId: 'general' },
  { en: 'Jira', pl: 'Jira', roleId: 'general' },
  { en: 'Kanban', pl: 'Kanban', roleId: 'general' },
  { en: 'Leadership', pl: 'Przywództwo', roleId: 'general' },
  { en: 'Mentoring', pl: 'Mentoring', roleId: 'general' },
  { en: 'Negotiation', pl: 'Negocjacje', roleId: 'general' },
  { en: 'Presentation Skills', pl: 'Umiejętności Prezentacyjne', roleId: 'general' },
  { en: 'Problem Solving', pl: 'Rozwiązywanie Problemów', roleId: 'general' },
  { en: 'Project Planning', pl: 'Planowanie Projektu', roleId: 'general' },
  { en: 'Scrum', pl: 'Scrum', roleId: 'general' },
  { en: 'Stakeholder Management', pl: 'Zarządzanie Interesariuszami', roleId: 'general' },
  { en: 'Technical Writing', pl: 'Pisanie Techniczne', roleId: 'general' },
  { en: 'Time Management', pl: 'Zarządzanie Czasem', roleId: 'general' },
  { en: 'Unit Testing', pl: 'Testy Jednostkowe', roleId: 'general' },

  // —— Frontend ——
  { en: 'Accessibility (a11y)', pl: 'Dostępność (a11y)', roleId: 'frontend' },
  { en: 'Angular', pl: 'Angular', roleId: 'frontend' },
  { en: 'CSS', pl: 'CSS', roleId: 'frontend' },
  { en: 'HTML', pl: 'HTML', roleId: 'frontend' },
  { en: 'JavaScript', pl: 'JavaScript', roleId: 'frontend' },
  { en: 'Jest', pl: 'Jest', roleId: 'frontend' },
  { en: 'Next.js', pl: 'Next.js', roleId: 'frontend' },
  { en: 'Performance Optimization', pl: 'Optymalizacja Wydajności', roleId: 'frontend' },
  { en: 'React', pl: 'React', roleId: 'frontend' },
  { en: 'Redux', pl: 'Redux', roleId: 'frontend' },
  { en: 'Responsive Design', pl: 'Responsywny Design', roleId: 'frontend' },
  { en: 'Storybook', pl: 'Storybook', roleId: 'frontend' },
  { en: 'Svelte', pl: 'Svelte', roleId: 'frontend' },
  { en: 'Tailwind CSS', pl: 'Tailwind CSS', roleId: 'frontend' },
  { en: 'TypeScript', pl: 'TypeScript', roleId: 'frontend' },
  { en: 'Vite', pl: 'Vite', roleId: 'frontend' },
  { en: 'Vue.js', pl: 'Vue.js', roleId: 'frontend' },
  { en: 'Webpack', pl: 'Webpack', roleId: 'frontend' },
  { en: 'Zustand', pl: 'Zustand', roleId: 'frontend' },

  // —— Backend ——
  { en: 'C#', pl: 'C#', roleId: 'backend' },
  { en: 'C++', pl: 'C++', roleId: 'backend' },
  { en: 'Django', pl: 'Django', roleId: 'backend' },
  { en: 'Event-Driven Architecture', pl: 'Architektura Event-Driven', roleId: 'backend' },
  { en: 'FastAPI', pl: 'FastAPI', roleId: 'backend' },
  { en: 'Go', pl: 'Go', roleId: 'backend' },
  { en: 'GraphQL', pl: 'GraphQL', roleId: 'backend' },
  { en: 'gRPC', pl: 'gRPC', roleId: 'backend' },
  { en: 'Java', pl: 'Java', roleId: 'backend' },
  { en: 'Laravel', pl: 'Laravel', roleId: 'backend' },
  { en: 'Microservices', pl: 'Mikroserwisy', roleId: 'backend' },
  { en: 'MongoDB', pl: 'MongoDB', roleId: 'backend' },
  { en: 'MySQL', pl: 'MySQL', roleId: 'backend' },
  { en: 'NestJS', pl: 'NestJS', roleId: 'backend' },
  { en: 'Node.js', pl: 'Node.js', roleId: 'backend' },
  { en: 'PHP', pl: 'PHP', roleId: 'backend' },
  { en: 'PostgreSQL', pl: 'PostgreSQL', roleId: 'backend' },
  { en: 'Python', pl: 'Python', roleId: 'backend' },
  { en: 'RabbitMQ', pl: 'RabbitMQ', roleId: 'backend' },
  { en: 'Redis', pl: 'Redis', roleId: 'backend' },
  { en: 'REST APIs', pl: 'REST API', roleId: 'backend' },
  { en: 'Ruby', pl: 'Ruby', roleId: 'backend' },
  { en: 'Ruby on Rails', pl: 'Ruby on Rails', roleId: 'backend' },
  { en: 'Rust', pl: 'Rust', roleId: 'backend' },
  { en: 'Spring Boot', pl: 'Spring Boot', roleId: 'backend' },
  { en: 'SQL', pl: 'SQL', roleId: 'backend' },
  { en: 'WebSockets', pl: 'WebSockets', roleId: 'backend' },

  // —— Full stack / platform / cloud / architecture ——
  { en: 'API Design', pl: 'Projektowanie API', roleId: 'fullstack' },
  { en: 'Domain-Driven Design', pl: 'Domain-Driven Design', roleId: 'fullstack' },
  { en: 'System Design', pl: 'Projektowanie Systemów', roleId: 'solutions-arch' },

  // —— Mobile ——
  { en: 'Android', pl: 'Android', roleId: 'mobile' },
  { en: 'Dart', pl: 'Dart', roleId: 'mobile' },
  { en: 'Flutter', pl: 'Flutter', roleId: 'mobile' },
  { en: 'iOS', pl: 'iOS', roleId: 'mobile' },
  { en: 'Jetpack Compose', pl: 'Jetpack Compose', roleId: 'mobile' },
  { en: 'Kotlin', pl: 'Kotlin', roleId: 'mobile' },
  { en: 'React Native', pl: 'React Native', roleId: 'mobile' },
  { en: 'Swift', pl: 'Swift', roleId: 'mobile' },
  { en: 'SwiftUI', pl: 'SwiftUI', roleId: 'mobile' },

  // —— DevOps / platform / cloud ——
  { en: 'Amazon Web Services (AWS)', pl: 'Amazon Web Services (AWS)', roleId: 'devops' },
  { en: 'Ansible', pl: 'Ansible', roleId: 'devops' },
  { en: 'Azure', pl: 'Azure', roleId: 'devops' },
  { en: 'CI/CD', pl: 'CI/CD', roleId: 'devops' },
  { en: 'Docker', pl: 'Docker', roleId: 'devops' },
  { en: 'GitHub Actions', pl: 'GitHub Actions', roleId: 'devops' },
  { en: 'GitLab CI', pl: 'GitLab CI', roleId: 'devops' },
  { en: 'Google Cloud Platform (GCP)', pl: 'Google Cloud Platform (GCP)', roleId: 'cloud' },
  { en: 'Grafana', pl: 'Grafana', roleId: 'devops' },
  { en: 'Helm', pl: 'Helm', roleId: 'devops' },
  { en: 'Infrastructure as Code', pl: 'Infrastructure as Code', roleId: 'platform' },
  { en: 'Kubernetes', pl: 'Kubernetes', roleId: 'devops' },
  { en: 'Linux', pl: 'Linux', roleId: 'devops' },
  { en: 'Nginx', pl: 'Nginx', roleId: 'devops' },
  { en: 'Observability', pl: 'Obserwowalność', roleId: 'platform' },
  { en: 'Prometheus', pl: 'Prometheus', roleId: 'devops' },
  { en: 'Site Reliability Engineering', pl: 'Site Reliability Engineering', roleId: 'devops' },
  { en: 'Terraform', pl: 'Terraform', roleId: 'devops' },

  // —— Data engineering / analytics ——
  { en: 'Airflow', pl: 'Airflow', roleId: 'data-eng' },
  { en: 'Apache Spark', pl: 'Apache Spark', roleId: 'data-eng' },
  { en: 'BigQuery', pl: 'BigQuery', roleId: 'data-eng' },
  { en: 'dbt', pl: 'dbt', roleId: 'analytics-eng' },
  { en: 'Elasticsearch', pl: 'Elasticsearch', roleId: 'data-eng' },
  { en: 'ETL', pl: 'ETL', roleId: 'data-eng' },
  { en: 'Kafka', pl: 'Kafka', roleId: 'data-eng' },
  { en: 'Snowflake', pl: 'Snowflake', roleId: 'data-eng' },

  // —— Data science / ML / AI ——
  { en: 'Computer Vision', pl: 'Computer Vision', roleId: 'ml' },
  { en: 'Deep Learning', pl: 'Deep Learning', roleId: 'ml' },
  { en: 'Feature Engineering', pl: 'Feature Engineering', roleId: 'ds' },
  { en: 'Hugging Face', pl: 'Hugging Face', roleId: 'ai-eng' },
  { en: 'Large Language Models (LLMs)', pl: 'Duże Modele Językowe (LLM)', roleId: 'ai-eng' },
  { en: 'MLOps', pl: 'MLOps', roleId: 'mlops' },
  { en: 'Natural Language Processing', pl: 'Przetwarzanie Języka Naturalnego', roleId: 'ml' },
  { en: 'NumPy', pl: 'NumPy', roleId: 'ds' },
  { en: 'Pandas', pl: 'Pandas', roleId: 'ds' },
  { en: 'Prompt Engineering', pl: 'Prompt Engineering', roleId: 'ai-eng' },
  { en: 'PyTorch', pl: 'PyTorch', roleId: 'ml' },
  { en: 'R', pl: 'R', roleId: 'ds' },
  { en: 'RAG', pl: 'RAG', roleId: 'ai-eng' },
  { en: 'scikit-learn', pl: 'scikit-learn', roleId: 'ds' },
  { en: 'Statistics', pl: 'Statystyka', roleId: 'ds' },
  { en: 'TensorFlow', pl: 'TensorFlow', roleId: 'ml' },

  // —— BI / analytics ——
  { en: 'Looker', pl: 'Looker', roleId: 'bi-dev' },
  { en: 'Microsoft Excel', pl: 'Microsoft Excel', roleId: 'analyst' },
  { en: 'Power BI', pl: 'Power BI', roleId: 'bi-dev' },
  { en: 'Tableau', pl: 'Tableau', roleId: 'analyst' },

  // —— QA / testing ——
  { en: 'Appium', pl: 'Appium', roleId: 'test-automation' },
  { en: 'Cypress', pl: 'Cypress', roleId: 'sdet' },
  { en: 'Manual Testing', pl: 'Testy Manualne', roleId: 'qa-eng' },
  { en: 'Playwright', pl: 'Playwright', roleId: 'test-automation' },
  { en: 'Regression Testing', pl: 'Testy Regresyjne', roleId: 'qa-eng' },
  { en: 'Selenium', pl: 'Selenium', roleId: 'sdet' },
  { en: 'Test Automation', pl: 'Automatyzacja Testów', roleId: 'test-automation' },
  { en: 'Test Strategy', pl: 'Strategia Testowa', roleId: 'qa-lead' },

  // —— Security ——
  { en: 'IAM', pl: 'IAM', roleId: 'security-eng' },
  { en: 'Incident Response', pl: 'Reagowanie na Incydenty', roleId: 'soc-analyst' },
  { en: 'OWASP', pl: 'OWASP', roleId: 'appsec' },
  { en: 'Penetration Testing', pl: 'Testy Penetracyjne', roleId: 'pentester' },
  { en: 'SIEM', pl: 'SIEM', roleId: 'soc-analyst' },
  { en: 'Threat Modeling', pl: 'Modelowanie Zagrożeń', roleId: 'appsec' },
  { en: 'Vulnerability Assessment', pl: 'Ocena Podatności', roleId: 'sec-analyst' },

  // —— Embedded ——
  { en: 'C (Embedded)', pl: 'C (Embedded)', roleId: 'embedded' },
  { en: 'FreeRTOS', pl: 'FreeRTOS', roleId: 'embedded' },
  { en: 'IoT', pl: 'IoT', roleId: 'embedded' },

  // —— Product / design ——
  { en: 'Design Systems', pl: 'Systemy Projektowe', roleId: 'ui' },
  { en: 'Figma', pl: 'Figma', roleId: 'ux' },
  { en: 'OKRs', pl: 'OKR', roleId: 'pm' },
  { en: 'PRD Writing', pl: 'Pisanie PRD', roleId: 'pm' },
  { en: 'Product Discovery', pl: 'Odkrywanie Produktu', roleId: 'pm' },
  { en: 'Prototyping', pl: 'Prototypowanie', roleId: 'product-designer' },
  { en: 'Roadmapping', pl: 'Roadmapowanie', roleId: 'pm' },
  { en: 'User Interviews', pl: 'Wywiady z Użytkownikami', roleId: 'ux-researcher' },
  { en: 'User Research', pl: 'Badania Użytkowników', roleId: 'ux-researcher' },
  { en: 'Usability Testing', pl: 'Testy Użyteczności', roleId: 'ux-researcher' },
  { en: 'Wireframing', pl: 'Wireframing', roleId: 'ux' },

  // —— Leadership / engineering management ——
  { en: 'Code Review', pl: 'Code Review', roleId: 'tech-lead' },
  { en: 'Engineering Strategy', pl: 'Strategia Inżynierska', roleId: 'eng-director' },
  { en: 'Hiring', pl: 'Rekrutacja Techniczna', roleId: 'em' },
  { en: 'People Management', pl: 'Zarządzanie Ludźmi', roleId: 'em' },
  { en: 'Technical Leadership', pl: 'Przywództwo Techniczne', roleId: 'staff-eng' },

  // —— Business / operations ——
  { en: 'Business Process Mapping', pl: 'Mapowanie Procesów Biznesowych', roleId: 'ba' },
  { en: 'Confluence', pl: 'Confluence', roleId: 'ba' },
  { en: 'Process Improvement', pl: 'Usprawnianie Procesów', roleId: 'ops-mgr' },
  { en: 'Requirements Gathering', pl: 'Zbieranie Wymagań', roleId: 'ba' },
  { en: 'Risk Management', pl: 'Zarządzanie Ryzykiem', roleId: 'project-mgr' },

  // —— Marketing / sales ——
  { en: 'B2B Sales', pl: 'Sprzedaż B2B', roleId: 'sales-rep' },
  { en: 'Content Strategy', pl: 'Strategia Contentu', roleId: 'content-marketing' },
  { en: 'Copywriting', pl: 'Copywriting', roleId: 'content-marketing' },
  { en: 'CRM', pl: 'CRM', roleId: 'sales-rep' },
  { en: 'Google Analytics', pl: 'Google Analytics', roleId: 'marketing-mgr' },
  { en: 'HubSpot', pl: 'HubSpot', roleId: 'marketing-mgr' },
  { en: 'Lead Generation', pl: 'Generowanie Leadów', roleId: 'sdr' },
  { en: 'Pipeline Management', pl: 'Zarządzanie Pipeline', roleId: 'account-exec' },
  { en: 'Salesforce', pl: 'Salesforce', roleId: 'account-exec' },
  { en: 'SEO', pl: 'SEO', roleId: 'seo-specialist' },
  { en: 'SEM', pl: 'SEM', roleId: 'seo-specialist' },

  // —— Finance ——
  { en: 'Budgeting', pl: 'Budżetowanie', roleId: 'fpa' },
  { en: 'Financial Modeling', pl: 'Modelowanie Finansowe', roleId: 'financial-analyst' },
  { en: 'Financial Reporting', pl: 'Raportowanie Finansowe', roleId: 'accountant' },
  { en: 'Forecasting', pl: 'Prognozowanie', roleId: 'fpa' },
  { en: 'GAAP', pl: 'GAAP', roleId: 'controller' },
  { en: 'IFRS', pl: 'IFRS', roleId: 'controller' },

  // —— HR / people ——
  { en: 'Applicant Tracking Systems', pl: 'Systemy ATS', roleId: 'recruiter' },
  { en: 'Employee Relations', pl: 'Relacje z Pracownikami', roleId: 'hr-bp' },
  { en: 'Interviewing', pl: 'Prowadzenie Rozmów Rekrutacyjnych', roleId: 'recruiter' },
  { en: 'Onboarding', pl: 'Onboarding', roleId: 'people-ops' },
  { en: 'Talent Sourcing', pl: 'Sourcing Talentów', roleId: 'talent-acquisition' },

  // —— Customer success / support ——
  { en: 'Account Management', pl: 'Zarządzanie Kontem', roleId: 'cs-mgr' },
  { en: 'Client Onboarding', pl: 'Onboarding Klienta', roleId: 'implementation' },
  { en: 'Customer Retention', pl: 'Retencja Klientów', roleId: 'cs-mgr' },
  { en: 'Help Desk', pl: 'Help Desk', roleId: 'support-spec' },
  { en: 'Troubleshooting', pl: 'Rozwiązywanie Problemów Technicznych', roleId: 'tech-support' },

  { en: 'Active Listening', pl: 'Aktywne Słuchanie', roleId: 'general' },
  { en: 'Adaptability', pl: 'Adaptacyjność', roleId: 'general' },
  { en: 'Attention to Detail', pl: 'Dbałość o Szczegóły', roleId: 'general' },
  { en: 'Calendar Management', pl: 'Zarządzanie Kalendarzem', roleId: 'general' },
  { en: 'Customer Service', pl: 'Obsługa Klienta', roleId: 'general' },
  { en: 'Data Entry', pl: 'Wprowadzanie Danych', roleId: 'general' },
  { en: 'Decision Making', pl: 'Podejmowanie Decyzji', roleId: 'general' },
  { en: 'Email Management', pl: 'Zarządzanie Pocztą E-mail', roleId: 'general' },
  { en: 'Emotional Intelligence', pl: 'Inteligencja Emocjonalna', roleId: 'general' },
  { en: 'Google Workspace', pl: 'Google Workspace', roleId: 'general' },
  { en: 'Microsoft Office', pl: 'Microsoft Office', roleId: 'general' },
  { en: 'Multitasking', pl: 'Wielozadaniowość', roleId: 'general' },
  { en: 'Organization', pl: 'Organizacja Pracy', roleId: 'general' },
  { en: 'Public Speaking', pl: 'Wystąpienia Publiczne', roleId: 'general' },
  { en: 'Reporting', pl: 'Raportowanie', roleId: 'general' },
  { en: 'Scheduling', pl: 'Planowanie Terminów', roleId: 'general' },
  { en: 'Teamwork', pl: 'Praca Zespołowa', roleId: 'general' },
  { en: 'Work Ethic', pl: 'Etyka Pracy', roleId: 'general' },

  // —— General & other roles ——
  { en: 'Administrative Support', pl: 'Wsparcie Administracyjne', roleId: 'admin-assistant' },
  { en: 'Budget Oversight', pl: 'Nadzór Budżetowy', roleId: 'general-mgr' },
  { en: 'Cross-team Coordination', pl: 'Koordynacja Między Zespołami', roleId: 'team-lead' },
  { en: 'Executive Support', pl: 'Wsparcie Zarządu', roleId: 'exec-assistant' },
  { en: 'Facility Coordination', pl: 'Koordynacja Biura', roleId: 'office-mgr' },
  { en: 'Front-line Support', pl: 'Wsparcie Pierwszej Linii', roleId: 'customer-service' },
  { en: 'Inventory Management', pl: 'Zarządzanie Zapasami', roleId: 'office-mgr' },
  { en: 'Meeting Coordination', pl: 'Organizacja Spotkań', roleId: 'admin-assistant' },
  { en: 'Office Administration', pl: 'Administracja Biurowa', roleId: 'admin-assistant' },
  { en: 'Operational Planning', pl: 'Planowanie Operacyjne', roleId: 'general-mgr' },
  { en: 'Travel Coordination', pl: 'Organizacja Podróży', roleId: 'exec-assistant' },

  // —— Healthcare ——
  { en: 'Clinical Documentation', pl: 'Dokumentacja Medyczna', roleId: 'nurse' },
  { en: 'HIPAA / RODO Compliance', pl: 'Zgodność HIPAA / RODO', roleId: 'healthcare-admin' },
  { en: 'Medical Terminology', pl: 'Terminologia Medyczna', roleId: 'medical-assistant' },
  { en: 'Patient Assessment', pl: 'Ocena Pacjenta', roleId: 'nurse' },
  { en: 'Patient Care', pl: 'Opieka nad Pacjentem', roleId: 'nurse' },
  { en: 'Patient Communication', pl: 'Komunikacja z Pacjentem', roleId: 'physician' },
  { en: 'Pharmacology', pl: 'Farmakologia', roleId: 'pharmacist' },
  { en: 'Rehabilitation Planning', pl: 'Planowanie Rehabilitacji', roleId: 'physiotherapist' },
  { en: 'Triage', pl: 'Triaging', roleId: 'nurse' },
  { en: 'Vital Signs Monitoring', pl: 'Monitorowanie Parametrów Życiowych', roleId: 'medical-assistant' },

  // —— Education ——
  { en: 'Assessment Design', pl: 'Projektowanie Ocen', roleId: 'teacher' },
  { en: 'Classroom Management', pl: 'Zarządzanie Klasą', roleId: 'teacher' },
  { en: 'Curriculum Development', pl: 'Tworzenie Programów Nauczania', roleId: 'teacher' },
  { en: 'E-learning', pl: 'E-learning', roleId: 'instructional-designer' },
  { en: 'Facilitation', pl: 'Facylitacja', roleId: 'training-specialist' },
  { en: 'Lesson Planning', pl: 'Planowanie Lekcji', roleId: 'teacher' },
  { en: 'Learning Design', pl: 'Projektowanie Nauczania', roleId: 'instructional-designer' },
  { en: 'Student Counseling', pl: 'Doradztwo Uczniowskie', roleId: 'school-counselor' },
  { en: 'Training Delivery', pl: 'Prowadzenie Szkoleń', roleId: 'training-specialist' },
  { en: 'Workshop Design', pl: 'Projektowanie Warsztatów', roleId: 'training-specialist' },

  // —— Legal & compliance ——
  { en: 'Case Management', pl: 'Zarządzanie Sprawami', roleId: 'paralegal' },
  { en: 'Contract Drafting', pl: 'Sporządzanie Umów', roleId: 'lawyer' },
  { en: 'Contract Review', pl: 'Przegląd Umów', roleId: 'legal-counsel' },
  { en: 'Due Diligence', pl: 'Due Diligence', roleId: 'compliance-officer' },
  { en: 'GDPR', pl: 'RODO', roleId: 'compliance-officer' },
  { en: 'Legal Research', pl: 'Badania Prawne', roleId: 'paralegal' },
  { en: 'Policy Development', pl: 'Tworzenie Polityk', roleId: 'compliance-officer' },
  { en: 'Regulatory Compliance', pl: 'Zgodność Regulacyjna', roleId: 'compliance-officer' },

  // —— Creative & media ——
  { en: 'Adobe Creative Suite', pl: 'Adobe Creative Suite', roleId: 'graphic-designer' },
  { en: 'Brand Guidelines', pl: 'Wytyczne Marki', roleId: 'brand-mgr' },
  { en: 'Canva', pl: 'Canva', roleId: 'graphic-designer' },
  { en: 'Color Theory', pl: 'Teoria Koloru', roleId: 'graphic-designer' },
  { en: 'Content Creation', pl: 'Tworzenie Treści', roleId: 'social-media-mgr' },
  { en: 'Copy Editing', pl: 'Redakcja Tekstu', roleId: 'journalist' },
  { en: 'Illustration', pl: 'Ilustracja', roleId: 'graphic-designer' },
  { en: 'Media Relations', pl: 'Relacje z Mediami', roleId: 'pr-specialist' },
  { en: 'Motion Graphics', pl: 'Motion Graphics', roleId: 'video-editor' },
  { en: 'Photography', pl: 'Fotografia', roleId: 'photographer' },
  { en: 'Press Releases', pl: 'Komunikaty Prasowe', roleId: 'pr-specialist' },
  { en: 'Storytelling', pl: 'Storytelling', roleId: 'journalist' },
  { en: 'Typography', pl: 'Typografia', roleId: 'graphic-designer' },
  { en: 'Video Production', pl: 'Produkcja Wideo', roleId: 'video-editor' },

  // —— Hospitality & tourism ——
  { en: 'Banquet Operations', pl: 'Obsługa Bankietów', roleId: 'event-planner' },
  { en: 'Booking Management', pl: 'Zarządzanie Rezerwacjami', roleId: 'travel-agent' },
  { en: 'Customer Hospitality', pl: 'Gościnność', roleId: 'hotel-mgr' },
  { en: 'Event Coordination', pl: 'Koordynacja Wydarzeń', roleId: 'event-planner' },
  { en: 'Food Safety', pl: 'Bezpieczeństwo Żywności', roleId: 'chef' },
  { en: 'Front Desk Operations', pl: 'Obsługa Recepcji', roleId: 'receptionist' },
  { en: 'Guest Relations', pl: 'Relacje z Gośćmi', roleId: 'hotel-mgr' },
  { en: 'Kitchen Management', pl: 'Zarządzanie Kuchnią', roleId: 'chef' },
  { en: 'Menu Planning', pl: 'Planowanie Menu', roleId: 'restaurant-mgr' },
  { en: 'Vendor Coordination', pl: 'Koordynacja Dostawców', roleId: 'event-planner' },

  // —— Retail & e-commerce ——
  { en: 'Assortment Planning', pl: 'Planowanie Asortymentu', roleId: 'buyer' },
  { en: 'Cash Handling', pl: 'Obsługa Kasy', roleId: 'store-mgr' },
  { en: 'Conversion Optimization', pl: 'Optymalizacja Konwersji', roleId: 'ecommerce-mgr' },
  { en: 'Inventory Replenishment', pl: 'Uzupełnianie Zapasów', roleId: 'merchandiser' },
  { en: 'Marketplace Management', pl: 'Zarządzanie Marketplace', roleId: 'ecommerce-mgr' },
  { en: 'Planogramming', pl: 'Planogramy', roleId: 'visual-merchandiser' },
  { en: 'Retail Sales', pl: 'Sprzedaż Detaliczna', roleId: 'store-mgr' },
  { en: 'Shopify', pl: 'Shopify', roleId: 'ecommerce-mgr' },
  { en: 'Store Operations', pl: 'Operacje Sklepowe', roleId: 'store-mgr' },
  { en: 'Visual Display', pl: 'Ekspozycja Wizualna', roleId: 'visual-merchandiser' },

  // —— Manufacturing & logistics ——
  { en: '5S / Lean Manufacturing', pl: '5S / Lean Manufacturing', roleId: 'production-supervisor' },
  { en: 'Demand Planning', pl: 'Planowanie Popytu', roleId: 'supply-chain' },
  { en: 'Freight Coordination', pl: 'Koordynacja Transportu', roleId: 'logistics-coord' },
  { en: 'Inventory Control', pl: 'Kontrola Zapasów', roleId: 'warehouse-supervisor' },
  { en: 'Production Planning', pl: 'Planowanie Produkcji', roleId: 'production-supervisor' },
  { en: 'Quality Control', pl: 'Kontrola Jakości', roleId: 'quality-inspector' },
  { en: 'Safety Procedures', pl: 'Procedury BHP', roleId: 'production-supervisor' },
  { en: 'Shipping & Receiving', pl: 'Przyjęcia i Wysyłki', roleId: 'warehouse-supervisor' },
  { en: 'Six Sigma', pl: 'Six Sigma', roleId: 'quality-inspector' },
  { en: 'Supplier Negotiation', pl: 'Negocjacje z Dostawcami', roleId: 'procurement' },
  { en: 'Warehouse Operations', pl: 'Operacje Magazynowe', roleId: 'warehouse-supervisor' },

  // —— Real estate & construction ——
  { en: 'AutoCAD', pl: 'AutoCAD', roleId: 'architect' },
  { en: 'Blueprint Reading', pl: 'Czytanie Rysunków Technicznych', roleId: 'civil-engineer' },
  { en: 'Building Codes', pl: 'Przepisy Budowlane', roleId: 'construction-pm' },
  { en: 'Client Negotiation', pl: 'Negocjacje z Klientem', roleId: 'real-estate-agent' },
  { en: 'Lease Management', pl: 'Zarządzanie Najmem', roleId: 'property-mgr' },
  { en: 'Property Inspection', pl: 'Inspekcja Nieruchomości', roleId: 'property-mgr' },
  { en: 'Property Marketing', pl: 'Marketing Nieruchomości', roleId: 'real-estate-agent' },
  { en: 'Site Supervision', pl: 'Nadzór Budowy', roleId: 'construction-pm' },
  { en: 'Structural Analysis', pl: 'Analiza Konstrukcyjna', roleId: 'civil-engineer' },
  { en: 'Tenant Relations', pl: 'Relacje z Najemcami', roleId: 'property-mgr' },

  // —— Public & social sector ——
  { en: 'Case Notes', pl: 'Notatki Case Management', roleId: 'social-worker' },
  { en: 'Community Outreach', pl: 'Praca z Społecznością', roleId: 'ngo-coordinator' },
  { en: 'Crisis Intervention', pl: 'Interwencja Kryzysowa', roleId: 'social-worker' },
  { en: 'Grant Writing', pl: 'Pisanie Wniosków Grantowych', roleId: 'ngo-coordinator' },
  { en: 'Program Management', pl: 'Zarządzanie Programem', roleId: 'ngo-coordinator' },
  { en: 'Public Policy Analysis', pl: 'Analiza Polityk Publicznych', roleId: 'policy-analyst' },
  { en: 'Stakeholder Engagement', pl: 'Zaangażowanie Interesariuszy', roleId: 'community-mgr' },
  { en: 'Volunteer Coordination', pl: 'Koordynacja Wolontariatu', roleId: 'ngo-coordinator' },

  // —— IT / infrastructure ——
  { en: 'Active Directory', pl: 'Active Directory', roleId: 'sysadmin' },
  { en: 'Cisco Networking', pl: 'Sieci Cisco', roleId: 'network-eng' },
  { en: 'Desktop Support', pl: 'Wsparcie Desktop', roleId: 'it-support' },
  { en: 'SAP ERP', pl: 'SAP ERP', roleId: 'sap-consultant' },
  { en: 'Windows Server', pl: 'Windows Server', roleId: 'sysadmin' },
].sort((a, b) => a.en.localeCompare(b.en));

export function findSkillByEn(en: string): SkillOption | undefined {
  return SKILLS.find((s) => s.en === en);
}
